import apiClient from './client';

/**
 * MED-975 (E1.3d) — Session Agenda Authoring API client.
 *
 * Backend: medley-api/routes/sessionAgenda.ts mounted under
 * /api/cohorts/:cohortId/sessions/:sessionId/agenda.
 *
 * Replaces the parsed-Google-Doc model used by MED-784. Coach prep / live
 * session rendering of this content is e1.3h (MED-976) and not in scope
 * here — this client serves only the CM authoring editor.
 */

export type SessionAgendaModality = 'group_coaching';

export interface SessionAgendaVisual {
  /** 1-based page number in the uploaded slide deck PDF. */
  slide_page: number;
  /** 1-based display order within the section. */
  sort_order: number;
}

export interface SessionAgendaLeaderPrompt {
  /** Present on existing rows; absent on new ones (server assigns). */
  id?: string;
  intro_text: string | null;
  /** Rich-text HTML serialized by the editor. */
  discussion_prompt: string | null;
  /** 1-based position; creation order per AC-PS5. */
  sort_order: number;
}

export interface SessionAgendaSection {
  /** Present on existing rows; absent on new ones (server assigns). */
  id?: string;
  section_name: string | null;
  duration_minutes: number | null;
  /** 1-based position in the agenda list (authoritative on save). */
  sort_order: number;
  /**
   * MED-980 (E1.3e / AC-PP). Rich-text HTML (bold, italic, underline,
   * bullet list) serialized by the editor. Sanitized server-side on write.
   * NULL when not authored.
   */
  participant_prompt: string | null;
  /**
   * MED-980 (E1.3e / AC-SCF). When true, this section is the
   * commitment-entry point in the participant live session (rendering is
   * E3.3a). UI gates the toggle on the bundle's
   * cohort_commitment_tracking_enabled flag.
   */
  show_commitment_field: boolean;
  /**
   * MED-980 (E1.3e / AC-SLC). When true, this section displays the
   * participant's prior-session commitment (rendering is E3.3a). Same UI
   * gating as above; additive to the pre-session E3.1b surface.
   */
  show_last_session_commitment: boolean;
  visuals: SessionAgendaVisual[];
  leader_prompts: SessionAgendaLeaderPrompt[];
}

/**
 * MED-981 (E1.3f / AC-RP). Reflection Prompt — plain-text question authored
 * on the Participant Pre-Session Work panel. NOT rich text per the spec.
 * `id` present on existing rows; absent on new ones (server assigns).
 */
export interface SessionAgendaReflectionPrompt {
  id?: string;
  prompt_text: string | null;
  sort_order: number;
}

/**
 * MED-981 (E1.3f). Participant Pre-Session Work panel — configures what
 * participants complete before the session. Always present on the agenda
 * bundle (defaults when no row exists yet); save serializes the same shape.
 *
 * Pre-session page rendering of this content is E3.1c (NOT this story).
 */
export interface SessionAgendaPreSessionWork {
  /** AC-CR1/CR3. UI gates the toggle on cohort_commitment_tracking_enabled. */
  commitment_review_enabled: boolean;
  /** AC-RT1/RT2. Sanitized rich-text HTML (B/I/U/bullet). Null when not authored. */
  reading_tasks: string | null;
  /** AC-RP1. Ordered repeatable list. */
  reflection_prompts: SessionAgendaReflectionPrompt[];
}

export interface SessionAgendaBundle {
  cohort_id: string;
  modality: SessionAgendaModality;
  session_number: number;
  /**
   * MED-1091 (E1.3n / AC5) — resolved session-number topic for the editor's
   * "Session Topic" field: session_number_config.topic, falling back to the
   * legacy cohort_session_topics topic name. The same value the View-by-Session
   * header shows — NOT sessions.title (the read/write mismatch this story fixes).
   * Null when no topic is set; the field renders the "Add session topic" empty
   * affordance (AC7).
   */
  topic: string | null;
  /** AC-SD2: multi-line plain-text description. */
  description: string | null;
  /**
   * MED-980 (E1.3e / AC-SCF2, AC-SLC2) + MED-981 (E1.3f / AC-CR2).
   * Cohort-level commitment_tracking_enabled (Flyway v201 / MED-806). When
   * false, the editor hides BOTH (a) the per-section commitment toggles
   * (e1.3e) and (b) the Enable Commitment Review toggle on the
   * pre-session work panel (e1.3f) entirely — not greyed, completely absent.
   */
  cohort_commitment_tracking_enabled: boolean;
  /**
   * MED-1074 (E1.3j / AC6). The configured session length for this cohort's
   * modality (`modalities.session_duration_minutes`, set at cohort setup). The
   * editor shows the agenda's running duration total alongside this in the
   * sticky header and warns (advisory, non-blocking) when the total exceeds it.
   * Null when the modality has no configured length — the editor then shows the
   * total alone (no `/ length` comparison) and suppresses the warning (AC7).
   */
  session_duration_minutes: number | null;
  /**
   * MED-1232 (E1.3 / AC6, AC8). The persisted one-way "Mark Complete" release
   * state. True once the CM has released this scope's content to the cohort:
   * the editor then renders the completed-state indicator instead of the Mark
   * Complete button, but the CM can still edit + Save and it stays complete.
   */
  marked_complete: boolean;
  /** MED-1232 — ISO timestamp of first release, or null while in progress. */
  completed_at: string | null;
  sections: SessionAgendaSection[];
  /** MED-981 (E1.3f) — Participant Pre-Session Work panel state. */
  participant_pre_session_work: SessionAgendaPreSessionWork;
}

/**
 * Full save payload — matches the GET response shape (minus the scope
 * triple) so the editor can round-trip without translation.
 */
export interface SessionAgendaSavePayload {
  description: string | null;
  sections: SessionAgendaSection[];
  participant_pre_session_work: SessionAgendaPreSessionWork;
}

/**
 * MED-982 (E1.3g) — One pickable source-session in the "Start from an
 * existing session" flow. Returned by `listDuplicateCandidates` and deduped
 * by `session_number` (the canonical authoring scope) — for a cohort with 3
 * groups, the picker shows one row per session_number, not 3.
 */
export interface SessionAgendaDuplicateCandidate {
  /** A representative session row id at this session_number. Pass verbatim back to `duplicateFrom` as `source_session_id`. */
  session_id: string;
  session_number: number;
  /** Session topic (canonical name shared across groups) when available, else the session's title. */
  title: string;
  /** AC-EP3 — false → render the row inactive/greyed in the picker with a hint. */
  has_authored_content: boolean;
}

/**
 * MED-982 (E1.3g). Slide-deck file metadata returned by `duplicateFrom` so
 * the editor knows the deck is already persisted server-side; the editor
 * refreshes its `files` list after a successful copy to render it.
 */
export interface SessionAgendaDuplicatedSlideDeck {
  id: string;
  slot: 'slide_deck';
  original_filename: string;
  mime_type: string;
  source_s3_key: string;
  converted_pdf_s3_key: string | null;
  processing_state: 'pending' | 'processing' | 'ready' | 'failed';
}

/**
 * MED-982 (E1.3g). Response from `duplicateFrom`. The agenda fields are NOT
 * persisted to the target until the editor's normal Save (AC-S1); the
 * frontend pours them straight into draft state. The slide deck IS already
 * persisted (AC-CB2) — `slide_deck_file` is the new target row, the source
 * remains untouched.
 *
 * AC-CB6: the commitment toggles on copied sections + the pre-session work's
 * commitment_review_enabled have been reconciled SERVER-side against the
 * target cohort's commitment_tracking_enabled (false → forced back to
 * defaults). The UI does not need to re-gate them — but `cohort_commitment_tracking_enabled`
 * is echoed in the response for the editor's own UI gating of the toggles
 * themselves.
 */
export interface SessionAgendaDuplicateFromResponse {
  description: string | null;
  sections: SessionAgendaSection[];
  participant_pre_session_work: SessionAgendaPreSessionWork;
  cohort_commitment_tracking_enabled: boolean;
  /** Null when the source had no slide deck. */
  slide_deck_file: SessionAgendaDuplicatedSlideDeck | null;
}

export const sessionAgendaApi = {
  /** GET /api/cohorts/:cohortId/sessions/:sessionId/agenda */
  get: async (cohortId: string, sessionId: string): Promise<SessionAgendaBundle> => {
    return apiClient.get<SessionAgendaBundle>(
      `/api/cohorts/${cohortId}/sessions/${sessionId}/agenda`,
    );
  },

  /** POST full-replace save. */
  save: async (
    cohortId: string,
    sessionId: string,
    payload: SessionAgendaSavePayload,
  ): Promise<SessionAgendaBundle> => {
    return apiClient.post<SessionAgendaBundle>(
      `/api/cohorts/${cohortId}/sessions/${sessionId}/agenda`,
      payload,
    );
  },

  /**
   * MED-1086 (E1.3m) — GET the agenda addressed by session number (no groups
   * required). Same bundle shape as `get`.
   */
  getByNumber: async (
    cohortId: string,
    sessionNumber: number,
  ): Promise<SessionAgendaBundle> => {
    return apiClient.get<SessionAgendaBundle>(
      `/api/cohorts/${cohortId}/sessions/by-number/${sessionNumber}/agenda`,
    );
  },

  /** MED-1086 (E1.3m) — POST full-replace save addressed by session number. */
  saveByNumber: async (
    cohortId: string,
    sessionNumber: number,
    payload: SessionAgendaSavePayload,
  ): Promise<SessionAgendaBundle> => {
    return apiClient.post<SessionAgendaBundle>(
      `/api/cohorts/${cohortId}/sessions/by-number/${sessionNumber}/agenda`,
      payload,
    );
  },

  /**
   * MED-1232 (E1.3 / AC2–AC5) — Mark the session content complete: the
   * explicit, one-way release to the cohort. The server re-checks the AC3
   * precondition (persisted agenda field-complete) and returns 422 if unmet.
   * Returns the fresh bundle (marked_complete=true) on success.
   */
  markComplete: async (
    cohortId: string,
    sessionId: string,
  ): Promise<SessionAgendaBundle> => {
    return apiClient.post<SessionAgendaBundle>(
      `/api/cohorts/${cohortId}/sessions/${sessionId}/agenda/complete`,
      {},
    );
  },

  /** MED-1232 (E1.3) — by-session-number Mark Complete (pre-groups entry point). */
  markCompleteByNumber: async (
    cohortId: string,
    sessionNumber: number,
  ): Promise<SessionAgendaBundle> => {
    return apiClient.post<SessionAgendaBundle>(
      `/api/cohorts/${cohortId}/sessions/by-number/${sessionNumber}/agenda/complete`,
      {},
    );
  },

  /**
   * MED-982 (E1.3g) — Picker step 2. List source-session candidates for a
   * cohort with a `has_authored_content` flag per row. Empty array when the
   * cohort has no group_coaching modality or no numbered sessions.
   */
  listDuplicateCandidates: async (
    cohortId: string,
  ): Promise<SessionAgendaDuplicateCandidate[]> => {
    return apiClient.get<SessionAgendaDuplicateCandidate[]>(
      `/api/cohorts/${cohortId}/sessions/duplicate-candidates`,
    );
  },

  /**
   * MED-982 (E1.3g) — Pre-populate the target editor from a source session.
   * Returns content for draft state PLUS new slide_deck file metadata
   * (already persisted server-side). The editor populates draft state from
   * the agenda fields and refreshes its content-files list to pick up the
   * new deck. Per AC-S1, the agenda is not committed until the editor's
   * normal Save.
   */
  duplicateFrom: async (
    cohortId: string,
    targetSessionId: string,
    sourceSessionId: string,
  ): Promise<SessionAgendaDuplicateFromResponse> => {
    return apiClient.post<SessionAgendaDuplicateFromResponse>(
      `/api/cohorts/${cohortId}/sessions/${targetSessionId}/agenda/duplicate-from`,
      { source_session_id: sourceSessionId },
    );
  },

  /**
   * MED-1110 — By-number variant of `duplicateFrom` for the pre-groups Build
   * Content editor (MED-1086 / E1.3m). Copies agenda content only — the
   * response's `slide_deck_file` is always null (the deck is sessionId-bound).
   * As with `duplicateFrom`, the agenda fields are not persisted until the
   * editor's by-number Save.
   */
  duplicateFromByNumber: async (
    cohortId: string,
    targetSessionNumber: number,
    sourceSessionId: string,
  ): Promise<SessionAgendaDuplicateFromResponse> => {
    return apiClient.post<SessionAgendaDuplicateFromResponse>(
      `/api/cohorts/${cohortId}/sessions/by-number/${targetSessionNumber}/agenda/duplicate-from`,
      { source_session_id: sourceSessionId },
    );
  },
};
